import { FC } from "react";
import { Autocomplete, TextField, InputAdornment } from "@mui/material";
import { SupervisorAccount } from "@mui/icons-material";
import { AssigneeDto, VpDto } from "../../api/Api";
import { useAssignees } from "../../features/tradingCounterparties/dataHooks/counterpartyQueries";

/** Entra group name resolved by the backend's AssigneeService group map. */
const VP_GROUP = "vp";

type Props = {
  value: VpDto | null;
  onChange: (vp: VpDto | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
};

/**
 * Picks exactly one VP from the Entra VP group. The selected VP is granted access to the
 * counterparty or broker, so the object id matters and not just the email: the grant table
 * keys on the oid.
 */
export const VpPicker: FC<Props> = ({
  value,
  onChange,
  label = "VP",
  required = false,
  disabled = false
}) => {
  const { data: assignees = [], isLoading } = useAssignees(VP_GROUP);

  const selected =
    assignees.find(
      (a) =>
        (!!value?.objectId && a.id === value.objectId) ||
        (!!value?.email && a.email?.toLowerCase() === value.email.toLowerCase())
    ) ?? null;

  const handleChange = (assignee: AssigneeDto | null) =>
    onChange(
      assignee
        ? {
            objectId: assignee.id,
            displayName: assignee.name,
            email: assignee.email
          }
        : null
    );

  return (
    <Autocomplete
      options={assignees.filter(
        (a) => !a.email?.includes("svc-it-keepit-backup")
      )}
      value={selected}
      loading={isLoading}
      disabled={disabled}
      onChange={(_, newValue) => handleChange(newValue)}
      getOptionLabel={(option) => `${option.name} (${option.email})`}
      isOptionEqualToValue={(option, selectedOption) =>
        option.id === selectedOption.id
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <SupervisorAccount />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  );
};
